import { getSummaryLanguage, getSummaryStyle, getTranscriptTerms } from './settings';
import { ApiError, apiRequest } from './api';
import type { AIChatTurn, ChatReplyDTO, GeneratedSummary, TranscriptSegmentDTO } from '../shared/types';
import { t } from './language';

/**
 * Yapay zekâ (Claude) çağrıları TalkNote sunucusunda — anahtar masaüstünde yok.
 * Burada yalnızca veri hazırlanıyor: tercihler ayarlardan okunuyor, transkript
 * bütün gidiyor (kırpılan kısım özete hiç giremiyor). İstem sunucuda kuruluyor
 * (web/lib/ai/prompts.ts).
 */

/** Tek bir mesajın ve toplam geçmişin sınırları — istek şişmesin. */
const MAX_TURNS = 40;
const MAX_TEXT_LENGTH = 8_000;
const MAX_CONTEXT_LENGTH = 20_000;

/** Sunucunun özet isteğinde kabul ettiği sınırlar (web/app/api/v1/ai/summarize/route.ts). */
const MAX_SEGMENTS = 20_000;
const MAX_SEGMENT_TEXT = 10_000;

/** Özet sunucuda yoğunlukta birkaç kez yeniden denenebiliyor — bekleme süresi uzun. */
const SUMMARY_TIMEOUT_MS = 120_000;
// Asistan yanıtlamadan önce toplantıları arayıp okuyabiliyor (birkaç model turu).
const CHAT_TIMEOUT_MS = 120_000;

/**
 * Sohbet geçmişini göndermeden önce buda. Renderer'dan gelen veri güvenilir
 * kabul edilmiyor: rol değerleri beyaz listeden geçiyor, metinler kırpılıyor
 * ve tur sayısı sınırlanıyor. Sunucu aynısını bir kez daha yapıyor.
 */
function sanitizeHistory(history: unknown): AIChatTurn[] {
  if (!Array.isArray(history)) return [];

  return history
    .filter((turn): turn is AIChatTurn =>
      Boolean(turn) &&
      typeof turn === 'object' &&
      typeof (turn as AIChatTurn).text === 'string' &&
      ((turn as AIChatTurn).role === 'user' || (turn as AIChatTurn).role === 'model')
    )
    .slice(-MAX_TURNS)
    .map((turn) => ({ role: turn.role, text: turn.text.slice(0, MAX_TEXT_LENGTH) }));
}

/** Sunucuya gidecek segmentler; sınırı aşan kayıtta başı ve sonu kalır, özet bir uca yığılmaz. */
function summarySegments(transcript: TranscriptSegmentDTO[]) {
  const half = MAX_SEGMENTS / 2;
  const picked = transcript.length <= MAX_SEGMENTS ? transcript : [...transcript.slice(0, half), ...transcript.slice(-half)];
  return picked.map((segment) => ({
    speaker: String(segment.speaker ?? '').slice(0, 200),
    timestamp: String(segment.timestamp ?? '').slice(0, 32),
    text: String(segment.text ?? '').slice(0, MAX_SEGMENT_TEXT),
  }));
}

/** Transkriptten başlık, yönetici özeti, kilit başlıklar, kararlar ve aksiyon maddeleri. */
export async function summarizeTranscript(
  transcript: TranscriptSegmentDTO[],
  title: string,
  /** `recording`: "Sen"/"Katılımcı" etiketleri; `bot`: gerçek katılımcı adları. */
  speakerMode: 'recording' | 'bot' = 'recording'
): Promise<GeneratedSummary> {
  if (!Array.isArray(transcript) || transcript.length === 0) {
    throw new Error(t('Özetlenecek transkript yok.', 'There is no transcript to summarize.'));
  }

  const [language, style, terms] = await Promise.all([getSummaryLanguage(), getSummaryStyle(), getTranscriptTerms()]);

  try {
    const { summary } = await apiRequest<{ summary: GeneratedSummary }>('POST', '/api/v1/ai/summarize', {
      body: {
        transcript: summarySegments(transcript),
        title: String(title ?? '').slice(0, 300),
        speakerMode,
        language,
        templateId: style.templateId,
        rules: style.rules,
        terms,
      },
      timeoutMs: SUMMARY_TIMEOUT_MS,
    });
    return summary;
  } catch (err) {
    console.error('[ai] özet isteği başarısız:', (err as Error).message);
    throw new Error(t(`Özet üretilemedi: ${(err as Error).message}`, `Could not generate the summary: ${(err as Error).message}`));
  }
}

/**
 * AI sohbeti. Toplantı bağlamını sunucu kullanıcının son toplantılarından
 * kendisi kuruyor (SEC-09); yalnızca kayıt sırasında sorulan sorularda süren
 * kaydın canlı transkripti gönderiliyor (sunucuda henüz yok).
 */
export async function chat(history: unknown, liveTranscript?: unknown): Promise<string> {
  const turns = sanitizeHistory(history);
  if (turns.length === 0) return '';

  const live = typeof liveTranscript === 'string' && liveTranscript.trim() ? liveTranscript.slice(-MAX_CONTEXT_LENGTH) : undefined;

  try {
    const { reply } = await apiRequest<{ reply: string }>('POST', '/api/v1/ai/chat', {
      body: { history: turns, ...(live ? { liveTranscript: live } : {}) },
      timeoutMs: CHAT_TIMEOUT_MS,
    });
    return reply ?? '';
  } catch (err) {
    console.error('[ai] sohbet isteği başarısız:', (err as Error).message);
    throw new Error(t(`AI yanıtı alınamadı: ${(err as Error).message}`, `Could not get an AI response: ${(err as Error).message}`));
  }
}

/**
 * Geçmişli sohbet (migration 0056): yalnızca yeni mesaj gider; geçmişi sunucu kayıtlı sohbetten
 * kurar ve konuşmayı şifreli saklar. `threadId` yoksa yeni sohbet açılır. Sunucu sınırı doldu
 * (409) ya da sohbet yoksa (404) mesajı olduğu gibi kullanıcıya gösterilir.
 */
export async function chatSend(threadId: unknown, text: unknown): Promise<ChatReplyDTO> {
  const message = typeof text === 'string' ? text.slice(0, MAX_TEXT_LENGTH) : '';
  if (!message.trim()) return { reply: '', threadId: typeof threadId === 'string' ? threadId : null, title: null, saved: false };

  try {
    const res = await apiRequest<Partial<ChatReplyDTO>>('POST', '/api/v1/ai/chat', {
      body: { text: message, ...(typeof threadId === 'string' && threadId ? { threadId } : {}) },
      timeoutMs: CHAT_TIMEOUT_MS,
    });
    return {
      reply: res.reply ?? '',
      threadId: typeof res.threadId === 'string' ? res.threadId : null,
      title: typeof res.title === 'string' ? res.title : null,
      saved: res.saved === true,
    };
  } catch (err) {
    // Geçmişli kipi bilmeyen eski sunucu (yalnızca `history` bekler) şema hatası verir:
    // konuşma kaydedilmeden eski yolla yanıtlanır, istemci sohbeti bellekte sürdürür.
    if (err instanceof ApiError && err.status === 400 && err.message.includes('Geçersiz sohbet isteği')) {
      return { reply: await chat([{ role: 'user', text: message }]), threadId: null, title: null, saved: false };
    }
    console.error('[ai] sohbet isteği başarısız:', (err as Error).message);
    throw new Error(t(`AI yanıtı alınamadı: ${(err as Error).message}`, `Could not get an AI response: ${(err as Error).message}`));
  }
}
